"use client";

import React, { useRef, useState, useEffect } from "react";
import Image from "next/image";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { Icon } from "@iconify/react";

interface GuideItem {
  img: string;
  name: string;
  role?: string;
  badge?: string;
  experience?: string;
  specialty?: string;
}

const cleanText = (str: string = "") =>
  str
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export default function ExpertGuides() {
  const [guides, setGuides] = useState<GuideItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchDynamicGuides = async () => {
      try {
        const backendUrl =
          process.env.NEXT_PUBLIC_BACKEND_URL ||
          "https://api.infinityadventurenepal.com";
        const response = await fetch(`${backendUrl}/api/users`);
        if (!response.ok) {
          throw new Error("Failed to fetch guides");
        }

        const result = await response.json();
        if (result?.data && Array.isArray(result.data)) {
          // Filter strictly for mountain, expedition, and trekking guides
          const guideUsers = result.data
            .filter((u: any) => {
              const role = (u.role || "").toLowerCase();
              const pos = cleanText(u.position).toLowerCase();

              // Exclude admin, office staff, board of directors, country reps, cooks, and porters
              if (
                role === "porter" ||
                role === "cook" ||
                role === "super-admin"
              )
                return false;
              if (
                /director|assistance|partner|transport|office|representative|consultant/i.test(
                  pos,
                )
              )
                return false;

              // Include verified mountain and trekking guides
              return (
                role === "expedition-guide" ||
                role === "trekking-guide" ||
                /guide|mountaineer|summiteer/i.test(pos)
              );
            })
            .sort((a: any, b: any) => (a.order || 99) - (b.order || 99));

          if (guideUsers.length > 0 && isMounted) {
            const mapped: GuideItem[] = guideUsers.map((u: any) => {
              const first = cleanText(u.firstName);
              const last = cleanText(u.lastName);
              const fullName = `${first} ${last}`.trim();

              // Custom uploaded photo check for Ivan, else Cloudinary coverPic
              let pic = u.coverPic || "/Guides/Guide.jpg";
              if (fullName.toLowerCase().includes("ivan")) {
                pic = "/Guides/guide8.png";
              }

              // Strictly use dynamic database fields only. If data is absent, omit it.
              return {
                img: pic,
                name: fullName,
                role: u.position ? cleanText(u.position) : undefined,
                badge: u.badge ? cleanText(u.badge) : undefined,
                experience: u.experience ? cleanText(u.experience) : undefined,
                specialty: u.desc ? cleanText(u.desc) : undefined,
              };
            });

            setGuides(mapped);
          }
        }
      } catch (error) {
        // Fallback in case API is offline
        if (isMounted) {
          setGuides([
            {
              img: "/Guides/Guide.jpg",
              name: "Prem Gurung",
              role: "IFMGA Guide Nepal & Founder",
            },
            {
              img: "/Guides/guide8.png",
              name: "Ivan Pegorari",
              role: "IFMGA Guide Italy",
            },
            {
              img: "/Guides/guide3.webp",
              name: "Sonam Lama",
              role: "Mountaineers (Manaslu & Everest Summiteer)",
            },
            {
              img: "/Guides/guide1.webp",
              name: "Pemba Sherpa",
              role: "Professional Trekking Guide",
            },
          ]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDynamicGuides();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    speed: 500,
    arrows: false,
  };

  return (
    <section className="w-full py-16 md:py-24 bg-white relative">
      <div className="lg:w-11/12 w-11/12 3xl:w-8/12 mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <h2 className="title font-extrabold uppercase text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-zinc-900 tracking-tight">
            Our Mountain Guides
          </h2>
          <p className="text-zinc-600 text-sm sm:text-base md:text-lg mt-3 leading-relaxed">
            Internationally certified IFMGA guides and lifelong Sherpa
            summiteers with decades of experience leading expeditions across the
            world’s highest peaks.
          </p>
        </div>

        {/* Loading Skeleton */}
        {loading && (
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className="h-[420px] lg:h-[460px] rounded-xl bg-zinc-100 animate-pulse border border-zinc-200"
              />
            ))}
          </div>
        )}

        {/* Desktop & Tablet Grid (Top 4 Guides) */}
        {!loading && guides.length > 0 && (
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {guides.slice(0, 4).map((guide, idx) => (
              <div
                key={idx}
                className="group relative h-[420px] lg:h-[460px] rounded-xl overflow-hidden bg-zinc-950 border border-zinc-200 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col justify-end cursor-pointer"
              >
                {/* Photo */}
                <Image
                  src={guide.img}
                  alt={guide.name}
                  fill
                  quality={95}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 450px"
                  className="object-cover object-top brightness-90 group-hover:brightness-95 group-hover:scale-105 transition-all duration-700 ease-out"
                />

               
                {(guide.badge || guide.experience) && (
                  <div className="absolute top-3.5 left-3.5 right-3.5 z-20 flex items-center justify-between pointer-events-none">
                    {guide.badge ? (
                      <span className="bg-black/75 backdrop-blur-xs text-white text-[11px] font-semibold px-2.5 py-1 rounded-md border border-white/15 shadow-xs">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#E25C23] mr-1.5 align-middle" />
                        {guide.badge}
                      </span>
                    ) : (
                      <div />
                    )}
                    {guide.experience && (
                      <span className="bg-[#E25C23] text-white text-[11px] font-bold px-2.5 py-0.5 rounded-md shadow-xs">
                        {guide.experience}
                      </span>
                    )}
                  </div>
                )}

                {/* Bottom Details (Only rendered if dynamic data exists in database) */}
                <div className="relative z-10 p-5 bg-gradient-to-t from-black via-black/75 to-transparent pt-16">
                  {guide.role && (
                    <p className="text-[#E25C23] text-xs font-bold uppercase tracking-wider mb-1">
                      {guide.role}
                    </p>
                  )}

                  <h3 className="text-white text-xl lg:text-2xl font-bold title tracking-wide group-hover:text-orange-400 transition-colors">
                    {guide.name}
                  </h3>

                  {guide.specialty && (
                    <p className="text-zinc-300 text-xs mt-1.5 font-medium line-clamp-2 leading-relaxed">
                      {guide.specialty}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Mobile View: Clean Slider */}
        {!loading && guides.length > 0 && (
          <div className="sm:hidden flex flex-col items-center">
            <div className="w-full max-w-[22em]">
              <Slider {...settings} ref={sliderRef}>
                {guides.map((guide, idx) => (
                  <div key={idx} className="px-1 outline-none">
                    <div className="relative h-[420px] rounded-xl overflow-hidden bg-zinc-950 border border-zinc-200 shadow-md flex flex-col justify-end">
                      <Image
                        src={guide.img}
                        alt={guide.name}
                        fill
                        quality={95}
                        sizes="(max-width: 640px) 100vw, 450px"
                        className="object-cover object-top brightness-90"
                      />

                      {/* Top Badges (Only rendered if dynamic data exists in database) */}
                      {(guide.badge || guide.experience) && (
                        <div className="absolute top-3.5 left-3.5 right-3.5 z-20 flex items-center justify-between">
                          {guide.badge ? (
                            <span className="bg-black/75 backdrop-blur-xs text-white text-[11px] font-semibold px-2.5 py-1 rounded-md border border-white/15">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#E25C23] mr-1.5 align-middle" />
                              {guide.badge}
                            </span>
                          ) : (
                            <div />
                          )}
                          {guide.experience && (
                            <span className="bg-[#E25C23] text-white text-[11px] font-bold px-2.5 py-0.5 rounded-md">
                              {guide.experience}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Bottom Details (Only rendered if dynamic data exists in database) */}
                      <div className="relative z-10 p-5 bg-gradient-to-t from-black via-black/75 to-transparent pt-16">
                        {guide.role && (
                          <p className="text-[#E25C23] text-xs font-bold uppercase tracking-wider mb-1">
                            {guide.role}
                          </p>
                        )}

                        <h3 className="text-white text-xl font-bold title tracking-wide">
                          {guide.name}
                        </h3>

                        {guide.specialty && (
                          <p className="text-zinc-300 text-xs mt-1.5 font-medium leading-relaxed">
                            {guide.specialty}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>

            {/* Navigation Controls on Mobile */}
            <div className="flex items-center gap-4 mt-6">
              <button
                onClick={handlePrev}
                aria-label="Previous guide"
                className="cursor-pointer border border-zinc-200 rounded-md bg-white hover:bg-[#E25C23] hover:text-white hover:border-[#E25C23] text-zinc-800 p-2.5 transition-colors shadow-sm"
              >
                <Icon icon="iconamoon:arrow-left-2" className="w-5 h-5" />
              </button>
              <span className="text-xs font-medium text-zinc-500">
                Swipe to view
              </span>
              <button
                onClick={handleNext}
                aria-label="Next guide"
                className="cursor-pointer border border-zinc-200 rounded-md bg-white hover:bg-[#E25C23] hover:text-white hover:border-[#E25C23] text-zinc-800 p-2.5 transition-colors shadow-sm"
              >
                <Icon icon="iconamoon:arrow-right-2" className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
